// @ts-nocheck
import React, { useContext, useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from 'react'
import { withAITracking } from '@microsoft/applicationinsights-react-js'
import { reactPlugin } from '../../AppInsights'
import { useAuth } from '../../context/AuthContext'
import { usePermissions } from '../../context/PermissionsContext'
import { useParams } from 'react-router'
import apiFetch from '../../api/apiFetch'
import Spinner from '../../components/Spinner'
import KpiBar from './components/KpiBar'
import EditFootprintBanner from './components/EditFootprintBanner'
import HcpRow from './components/HcpRow'
import SearchBarV2, { EMPTY_FILTERS, applyFilters } from './components/SearchBarV2'
import StatusBar from './components/StatusBar'
import TargetTypeLegend from './components/TargetTypeLegend'
import ExerciseGuidelinesModal from './components/ExerciseGuidelinesModal'
import { deriveRowStatus, getFinalType, isDIR, isMOT, TYPE_ORDER } from './targetingUtils'
import styles from './css/TargetingV2.module.css'
import ColumnPicker from './components/ColumnPicker'

const STATUS_ORDER = { added: 0, dropped: 1, demoted: 2, promoted: 3, unchanged: 4 }
// Team-specific override for the "previous" target type column.
// Extend here for future teams with non-standard prior-cycle columns.
// PREV_TARGET_TYPE is populated via CUSTOM_COLUMNS spread for all current teams.
// Extend here if a future team requires a different prior-cycle column.
const TEAM_PREV_COL = {}
const CORE_COLS = new Set(['Multiple', 'CURRENT_TARGET_VALUE', 'CHANGED_TARGET_VALUE', 'SPEC1'])

// Minimum flex floors (%) for columns with long content. DB flex value wins if already larger.
const MIN_FLEX = { NPINUM: 8, UPDATE_REASON: 15, Multiple: 15, SPEC1: 8, DEGREE: 8, LAST_DIRECT_CALL: 8 }
// Fallback flex for columns with no DB-configured width (no-config / default path).
const DEFAULT_FLEX = 8

function getColValue(row, key, prevCol = 'PREV_TARGET_TYPE', prevFallbackCol = null) {
    if (key === 'name')      return `${row.LAST_NAME || ''} ${row.FIRST_NAME || ''}`.trim()
    if (key === 'specialty') return row.SPEC1 || ''
    if (key === 'proposed')  return row.CURRENT_TARGET_VALUE || ''
    if (key === 'final')     return row.TARGET_TYPE || row.CHANGED_TARGET_VALUE || row.CURRENT_TARGET_VALUE || ''
    if (key === 'status')       return String(STATUS_ORDER[deriveRowStatus(row, prevCol, prevFallbackCol)] ?? 99)
    if (key === 'actionReason') return row.REASON || ''
    return String(row[key] ?? '')
}

function sortRows(rows, override = { key: null, dir: 'asc' }, prevCol = 'PREV_TARGET_TYPE', prevFallbackCol = null) {
    if (override.key) {
        return [...rows].sort((a, b) => {
            const va = getColValue(a, override.key, prevCol, prevFallbackCol)
            const vb = getColValue(b, override.key, prevCol, prevFallbackCol)
            const cmp = va.localeCompare(vb, undefined, { numeric: true })
            return override.dir === 'asc' ? cmp : -cmp
        })
    }
    // Default: status group → final type → HCP name
    return [...rows].sort((a, b) => {
        const sa = STATUS_ORDER[deriveRowStatus(a, prevCol, prevFallbackCol)]
        const sb = STATUS_ORDER[deriveRowStatus(b, prevCol, prevFallbackCol)]
        if (sa !== sb) return sa - sb
        const fa = getFinalType(a) || ''
        const fb = getFinalType(b) || ''
        if (fa !== fb) return fa.localeCompare(fb)
        const na = `${a.LAST_NAME} ${a.FIRST_NAME}`
        const nb = `${b.LAST_NAME} ${b.FIRST_NAME}`
        return na.localeCompare(nb)
    })
}

function TargetingV2(props) {
    const { user } = useAuth()
    const { idSlug, terrSlug } = useParams()

    const exercise = props.exercise

    // exercise.vacantTerrID is set by App.js when the user is an XREF substitute covering
    // a different team's exercise (e.g. T21 rep on a T13 exercise). Using it here ensures
    // the correct territory is used for both display and all data fetches synchronously.
    const terrID = terrSlug || exercise?.vacantTerrID || (user.terr !== '' ? user.terr : String(exercise?.TEAM || ''))

    const permissions = usePermissions()
    const approved = props.approved || false

    const [viewMode, setViewMode] = useState(() =>
        localStorage.getItem(`targeting_v2_view_${user?.userName}`) || 'live'
    )
    const [status, setStatus]       = useState(null)
    const [perTargetTotals, setPerTargetTotals] = useState(null)
    const [lock, setLock]           = useState(true)
    const [options, setOptions] = useState({ upgrade: [], downgrade: [] })
    const [data, setData]       = useState([])
    const [baselineData, setBaselineData] = useState([])
    const [loaded, setLoaded]   = useState(false)
    // Guidelines popup: shown every time an exercise/territory is opened (unless approved), until the user clicks OK.
    const [showGuidelines, setShowGuidelines] = useState(true)
    useEffect(() => { setShowGuidelines(true) }, [idSlug, terrSlug])
    const [config, setConfig]   = useState(null)
    const [sortOverride, setSortOverride] = useState({ key: null, dir: 'asc' })
    const [recentlyTouched, setRecentlyTouched] = useState(new Set())
    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [stableOrder, setStableOrder] = useState([])
    // Full row snapshot at page load — used to revert DB state on undo (unlike baselineData which strips changes)
    const sessionStartMapRef = useRef(new Map())
    // Measures the sticky controls block height so thead top can match it
    const stickyControlsRef = useRef(null)
    const [controlsHeight, setControlsHeight] = useState(0)

    const [controlsCollapsed, setControlsCollapsed] = useState(false)

    // Column drag-and-drop
    const dragColRef = useRef(null)
    const [dragOverCol, setDragOverCol] = useState(null)

    // Column visibility
    const colVisKey = `targeting_v2_vis_${user?.userName}_${exercise?.ID}`
    const [hiddenCols, setHiddenCols] = useState(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(`targeting_v2_vis_${user?.userName}_${exercise?.ID}`) || 'null')
            return new Set(Array.isArray(stored) ? stored : [])
        } catch { return new Set() }
    })

    const handleToggleCol = (id) => {
        setHiddenCols(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            localStorage.setItem(colVisKey, JSON.stringify([...next]))
            return next
        })
    }

    const isDev = process.env.NODE_ENV === 'development'

    useLayoutEffect(() => {
        const el = stickyControlsRef.current
        if (!el) return
        const measure = () => setControlsHeight(el.getBoundingClientRect().height)
        measure()
        const ro = new ResizeObserver(measure)
        ro.observe(el)
        return () => ro.disconnect()
    }, [viewMode, loaded, status, controlsCollapsed])

    const getTotals = useCallback((terr) => {
        const t = terr || terrID
        apiFetch('/api/v2/targeting/get-current-totals', {
            user: user.userName,
            terr: t,
            id: exercise?.TARGETING_EXERCISE_ID,
            team: exercise?.TEAM,
            role: user.role
        }).then((rows) => {
            if (!Array.isArray(rows) || rows.length === 0) return
            const typeCounts = {}
            let STATUS_ID = 1
            rows.forEach((row, i) => {
                if (i === 0) STATUS_ID = row.STATUS_ID ?? 1
                if (row.TARGET_TYPE) {
                    typeCounts[row.TARGET_TYPE] = {
                        current: row.CURRENT_TOTAL ?? 0,
                        final: row.FINAL_TOTAL ?? 0,
                        startCall: row.START_CALL_TOTAL ?? 0,
                    }
                }
            })
            setStatus({ typeCounts, STATUS_ID })
        })
        apiFetch('/api/v2/targeting/get-per-target-totals', {
            id: exercise?.TARGETING_EXERCISE_ID,
            terr: t,
            allowDIR: !!(exercise?.PER_TARGET_LIMITS ? JSON.parse(exercise.PER_TARGET_LIMITS)?.allowDIR : false)
        }).then((d) => {
            if (d) setPerTargetTotals(d)
        })
    }, [terrID, exercise, user])

    const loadData = useCallback((terr) => {
        const t = terr || terrID
        setLoaded(false)
        apiFetch('/api/v2/targeting/get-source-table-data', {
            id: exercise?.ID,
            terr: t,
            type: exercise?.CUSTOMER_TYPE,
            USER_NAME: user.userName,
            role: user.role
        }).then((d) => {
            if (d?.length > 0) {
                const parsed = d.map(row => {
                    if (row.CUSTOM_COLUMNS) {
                        try { return { ...row, ...JSON.parse(row.CUSTOM_COLUMNS) } }
                        catch { return row }
                    }
                    return row
                })
                setData(parsed)
                sessionStartMapRef.current = new Map(parsed.map(r => [r.PROF_ID, r]))
                // Derive prevCol from exercise config now — config state may not be set yet
                // since it's populated in a separate useEffect. Mirrors prevCol logic.
                const teamOverride = TEAM_PREV_COL[Number(exercise?.TEAM)]
                let initialPrevCol = teamOverride ? teamOverride.col : 'PREV_TARGET_TYPE'
                const initialPrevFallbackCol = teamOverride ? teamOverride.fallback : null
                if (!teamOverride && exercise?.COLUMNS) {
                    try {
                        const cols = Object.values(JSON.parse(exercise.COLUMNS)).filter(c => c.show)
                        const hasPrev = cols.some(c => c.col === 'PREV_TARGET_TYPE')
                        if (!hasPrev) {
                            const typeCols = cols.filter(c =>
                                c.col.endsWith('_TARGET_TYPE') &&
                                !['CURRENT_TARGET_VALUE', 'CHANGED_TARGET_VALUE'].includes(c.col)
                            )
                            if (typeCols.length === 1) initialPrevCol = typeCols[0].col
                        }
                    } catch { /* ignore malformed config */ }
                }
                // Capture the default sort order once at load — used to keep rows stable after edits
                setStableOrder(sortRows(parsed, { key: null, dir: 'asc' }, initialPrevCol, initialPrevFallbackCol).map(r => r.PROF_ID))
                // Baseline always reflects Q1: strip any saved changes so it never drifts
                const baseline = parsed.map(({ CHANGED_TARGET_VALUE, TARGET_TYPE, ...rest }) => rest)
                setBaselineData(baseline)
            }
            setLoaded(true)
        })
    }, [terrID, exercise, user])

    useEffect(() => {
        if (exercise && terrID) {
            getTotals()
            loadData()
        }
    }, [exercise?.ID, terrID])

    useEffect(() => {
        if (exercise?.COLUMNS) {
            try {
                const parsed = JSON.parse(exercise.COLUMNS)
                setConfig(parsed)
            } catch { /* ignore malformed config */ }
        }
    }, [exercise?.ID])

    // Port of v1 status-lock rules from Search.js
    useEffect(() => {
        if (approved) { setLock(true); return }
        if (!status) return
        const sl = permissions.targeting.search.statusLocks
        if (sl.general.oneTwo.includes(user.role) && (status.STATUS_ID === 1 || status.STATUS_ID === 2)) {
            setLock(false)
        } else if (sl.general.threeFive.includes(user.role) && (status.STATUS_ID === 3 || status.STATUS_ID === 5)) {
            setLock(false)
        } else if (sl.admin.four.includes(user.role) && status.STATUS_ID === 4) {
            setLock(false)
        } else if (sl.admin.five.includes(user.role) && status.STATUS_ID === 5) {
            setLock(true)
        } else if (sl.general.five.includes(user.role) && status.STATUS_ID === 5) {
            setLock(false)
        } else {
            setLock(true)
        }
    }, [status, approved])

    // Fetch upgrade/downgrade reason codes — same as v1 Search.js
    useEffect(() => {
        if (exercise?.ID) {
            apiFetch('/api/v2/admin/exerciseadmin/get-reasons',
                { id: exercise.ID, type: exercise.CUSTOMER_TYPE }
            ).then((data) => {
                    const downgradeReasons = (data || [])
                        .filter(r => !r.IS_UPGRADE)
                        .map(r => r.REASON)
                        // When DIR is enabled, remove 'Deceased/Inactive/Retired' from the standard
                        // downgrade list — reps must use the DIR button for that action instead
                        .filter(r => !allowDIR || r !== 'Deceased/Inactive/Retired')
                    setOptions({
                        upgrade:   (data || []).filter(r => r.IS_UPGRADE).map(r => r.REASON),
                        downgrade: downgradeReasons,
                    })
                }
            )
        }
    }, [exercise?.ID])

    const handleResetToBaseline = () => {
        // Revert DB for each row touched this session back to its session-start state
        data.filter(r => recentlyTouched.has(r.PROF_ID)).forEach(r => {
            const start = sessionStartMapRef.current.get(r.PROF_ID)
            const restoreTarget = start?.TARGET_TYPE || start?.CHANGED_TARGET_VALUE || ''
            apiFetch('/api/v2/targeting/revert-target-type', {
                ID: exercise.TARGETING_EXERCISE_ID,
                terr: terrID,
                mdmID: r.PROF_ID,
                currentTarget: r.TARGET_TYPE || '',
                restoreTarget,
                role: user.role,
                username: user.userName,
            })
        })
        // Restore client state to full session-start snapshot (not stripped baseline)
        setData(prev => prev.map(r =>
            recentlyTouched.has(r.PROF_ID) ? (sessionStartMapRef.current.get(r.PROF_ID) || r) : r
        ))
        setRecentlyTouched(new Set())
        getTotals()
    }

    const handleRowUpdate = (updatedRow) => {
        setData(prev => prev.map(r => r.PROF_ID === updatedRow.PROF_ID ? updatedRow : r))
        setRecentlyTouched(prev => new Set([...prev, updatedRow.PROF_ID]))
    }

    const handleColumnReset = useCallback((profId) => {
        setRecentlyTouched(prev => {
            const next = new Set(prev)
            next.delete(profId)
            return next
        })
    }, [])

    const baselineMap = useMemo(
        () => new Map(baselineData.map(r => [r.PROF_ID, r])),
        [baselineData]
    )

    // Canonical column descriptor list — derived from exercise config or defaults
    const columnDefs = useMemo(() => {
        if (config) {
            const defs = Object.keys(config)
                .sort((a, b) => parseInt(a) - parseInt(b))
                .filter(k => config[k].show)
                .map(k => {
                    const c = config[k]
                    const sortKey =
                        c.col === 'Multiple'              ? 'name'
                        : c.col === 'SPEC1'               ? 'specialty'
                        : c.col === 'CURRENT_TARGET_VALUE'  ? 'proposed'
                        : c.col === 'CHANGED_TARGET_VALUE'  ? 'final'
                        : null
                    const align =
                        ['CURRENT_TARGET_VALUE', 'CHANGED_TARGET_VALUE', 'PREV_TARGET_TYPE'].includes(c.col) || c.col.endsWith('_TARGET_TYPE')
                            ? 'center' : 'left'
                    return { id: c.col, col: c.col, title: c.title, sortKey, align, options: c.options, width: c.flex != null ? c.flex + '%' : undefined }
                })
            defs.push({ id: '_action_reason', col: '_action_reason', title: 'Action Reason', sortKey: 'actionReason', align: 'left',  width: '110px' })
            defs.push({ id: '_status',        col: '_status',        title: 'Status',        sortKey: 'status',       align: 'left',  width: '85px'  })
            defs.push({ id: '_action',        col: '_action',        title: 'Action',                                 align: 'right', width: '110px' })
            return defs
        }
        return [
            { id: 'name',      col: 'Multiple',             title: 'HCP',      sortKey: 'name',      align: 'left'   },
            { id: 'specialty', col: 'SPEC1',                title: 'Specialty', sortKey: 'specialty', align: 'left'   },
            { id: 'proposed',  col: 'CURRENT_TARGET_VALUE', title: 'Proposed',  sortKey: 'proposed',  align: 'center' },
            { id: 'final',     col: 'CHANGED_TARGET_VALUE', title: 'Final',     sortKey: 'final',     align: 'center' },
            { id: '_action_reason', col: '_action_reason', title: 'Action Reason', sortKey: 'actionReason', align: 'left',  width: '110px' },
            { id: '_status',   col: '_status',              title: 'Status',        sortKey: 'status',       align: 'left',  width: '85px'  },
            { id: '_action',   col: '_action',              title: 'Action',                                 align: 'right', width: '110px' },
        ]
    }, [config])

    // Determine which column holds the previous cycle's target type.
    // Teams in TEAM_PREV_COL use team-specific columns; other exercises use PREV_TARGET_TYPE from config
    // or fall back to the single _TARGET_TYPE column when exactly one is present.
    const prevCol = useMemo(() => {
        const override = TEAM_PREV_COL[Number(exercise?.TEAM)]
        if (override) return override.col
        if (columnDefs.some(d => d.col === 'PREV_TARGET_TYPE')) return 'PREV_TARGET_TYPE'
        const typeCols = columnDefs.filter(d =>
            d.col.endsWith('_TARGET_TYPE') &&
            !['CURRENT_TARGET_VALUE', 'CHANGED_TARGET_VALUE'].includes(d.col)
        )
        return typeCols.length === 1 ? typeCols[0].col : 'PREV_TARGET_TYPE'
    }, [columnDefs, exercise?.TEAM])

    const prevFallbackCol = useMemo(() => {
        return TEAM_PREV_COL[Number(exercise?.TEAM)]?.fallback || null
    }, [exercise?.TEAM])

    const colOrderKey = `targeting_v2_cols_${user?.userName}_${exercise?.ID}`
    const [columnOrder, setColumnOrder] = useState(null)

    // Reconcile persisted column order with current column defs on mount / exercise/config change
    useEffect(() => {
        if (!columnDefs.length) return
        try {
            const stored = JSON.parse(localStorage.getItem(colOrderKey) || 'null')
            if (stored && Array.isArray(stored)) {
                const defIds = new Set(columnDefs.map(d => d.id))
                const valid   = stored.filter(id => defIds.has(id))
                const missing = columnDefs.map(d => d.id).filter(id => !valid.includes(id))
                setColumnOrder([...valid, ...missing])
            } else {
                setColumnOrder(columnDefs.map(d => d.id))
            }
        } catch {
            setColumnOrder(columnDefs.map(d => d.id))
        }
    }, [colOrderKey, columnDefs])

    const orderedColDefs = useMemo(() => {
        const ordered = columnOrder
            ? columnOrder.map(id => columnDefs.find(d => d.id === id)).filter(Boolean)
            : columnDefs
        const visible = ordered.filter(d => !hiddenCols.has(d.id))

        // Renormalize flex widths so each visible flex col gets its fair proportional share.
        // Fixed-px synthetic cols (_action, _status, _action_reason) are excluded from the pool.
        // Plain % widths are used — table-layout:fixed distributes the remainder after fixed-px
        // cols are allocated, so relative proportions are preserved without needing calc().
        const effectiveFlexFor = (d) => {
            const floor = MIN_FLEX[d.col] ?? 0
            const raw = d.width
            if (raw?.endsWith('px')) return 0                    // fixed-px col: excluded
            if (!raw) return Math.max(DEFAULT_FLEX, floor)       // no DB width: use floor or default
            return Math.max(parseFloat(raw), floor)
        }

        const visibleFlexTotal = visible.reduce((sum, d) => sum + effectiveFlexFor(d), 0)

        if (visibleFlexTotal === 0) return visible

        return visible.map(d => {
            if (d.width?.endsWith('px')) return d
            const flex = effectiveFlexFor(d)
            if (flex === 0) return d
            const newWidth = `${((flex / visibleFlexTotal) * 100).toFixed(4)}%`
            return { ...d, width: newWidth }
        })
    }, [columnOrder, columnDefs, hiddenCols])

    const handleSetViewMode = (mode) => {
        setViewMode(mode)
        localStorage.setItem(`targeting_v2_view_${user?.userName}`, mode)
    }

    const handleHeaderClick = (key) => {
        setSortOverride(prev => {
            if (prev.key === key) {
                if (prev.dir === 'asc') return { key, dir: 'desc' }
                return { key: null, dir: 'asc' }  // third click resets to default
            }
            return { key, dir: 'asc' }
        })
    }

    const sortIndicator = (key) => sortOverride.key === key
        ? (sortOverride.dir === 'asc' ? ' ▲' : ' ▼')
        : null

    const handleDragStart = (e, id) => {
        dragColRef.current = id
        e.dataTransfer.effectAllowed = 'move'
    }

    const handleDragOver = (e, id) => {
        e.preventDefault()
        e.dataTransfer.dropEffect = 'move'
        if (id !== dragColRef.current) setDragOverCol(id)
    }

    const handleDrop = (e, id) => {
        e.preventDefault()
        const from = dragColRef.current
        if (!from || from === id) { setDragOverCol(null); return }
        setColumnOrder(prev => {
            const next = [...(prev || columnDefs.map(d => d.id))]
            const fromIdx = next.indexOf(from)
            const toIdx   = next.indexOf(id)
            if (fromIdx === -1 || toIdx === -1) return prev
            next.splice(fromIdx, 1)
            next.splice(toIdx, 0, from)
            localStorage.setItem(colOrderKey, JSON.stringify(next))
            return next
        })
        setDragOverCol(null)
        dragColRef.current = null
    }

    const handleDragEnd = () => {
        setDragOverCol(null)
        dragColRef.current = null
    }

    const defaultColumnOrder = useMemo(() => columnDefs.map(d => d.id), [columnDefs])

    const isColumnCustomized = useMemo(() => {
        if (!columnOrder) return false
        return columnOrder.join(',') !== defaultColumnOrder.join(',') || hiddenCols.size > 0
    }, [columnOrder, defaultColumnOrder, hiddenCols])

    const handleResetColumns = () => {
        localStorage.removeItem(colOrderKey)
        localStorage.removeItem(colVisKey)
        setColumnOrder(defaultColumnOrder)
        setHiddenCols(new Set())
    }

    const handleExport = () => {
        if (!config) return
        const csvCell = (val) => {
            const str = String(val ?? '')
            // Quote any cell containing a comma, double-quote, newline, or carriage return
            if (str.includes('"') || str.includes(',') || str.includes('\n') || str.includes('\r')) {
                return '"' + str.replace(/"/g, '""') + '"'
            }
            return str
        }
        const configCols = Object.keys(config)
            .sort((a, b) => parseInt(a) - parseInt(b))
            .filter(k => config[k].show)
            .map(k => config[k])
        const headers = configCols.map(c => c.col === 'Multiple' ? 'NAME' : c.col)
        const multipleIdx = headers.indexOf('NAME')
        if (multipleIdx !== -1) {
            headers.splice(multipleIdx + 1, 0, 'ADDRESS', 'CITY', 'STATE', 'ZIP_CODE')
        }
        headers.push('ACTION_REASON', 'STATUS')
        const rows = [headers.map(csvCell).join(',')]
        for (const row of activeData) {
            const values = configCols.map(c => {
                const col = c.col
                if (col === 'Multiple') {
                    return [
                        csvCell(`${row.FIRST_NAME || ''} ${row.LAST_NAME || ''}`.trim()),
                        csvCell(row.ADDRESS || ''),
                        csvCell(row.CITY || ''),
                        csvCell(row.STATE || ''),
                        csvCell(row.ZIP_CODE || ''),
                    ].join(',')
                }
                if (col === 'CHANGED_TARGET_VALUE') return csvCell(row.TARGET_TYPE || '')
                if (col === 'CURRENT_ADDRESS') return csvCell(`${row.PRIMARY_ADDRESS1 || ''} ${row.PRIMARY_CITY || ''} ${row.PRIMARY_STATE || ''} ${row.PRIMARY_ZIP5 || ''}`.trim())
                if (col === 'BEST_ADDRESS') return csvCell(`${row.BEST_ADDRESS1 || ''} ${row.BEST_CITY || ''} ${row.BEST_STATE || ''} ${row.BEST_ZIP5 || ''}`.trim())
                return csvCell(row[col] ?? '')
            })
            values.push(csvCell(row.REASON || ''), csvCell(deriveRowStatus(row, prevCol, prevFallbackCol)))
            rows.push(values.join(','))
        }
        const csv = rows.join('\r\n')
        const fileName = `${exerciseName || 'Targeting'} - TERRITORY ${terrID}.csv`
        const blob = new Blob([csv], { type: 'text/csv' })
        const href = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = href
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(href)
    }

    // "Your edits this session" = only rows the user touched this page load, compared to baseline
    let sessionAdded = 0, sessionDropped = 0, sessionChanged = 0
    data.filter(r => recentlyTouched.has(r.PROF_ID)).forEach(r => {
        const base = baselineMap.get(r.PROF_ID)
        const baseQ2 = base ? getFinalType(base) : null
        const nowQ2  = getFinalType(r)
        if (baseQ2 === nowQ2) return  // reverted to original — don't count
        const nowStatus = deriveRowStatus(r, prevCol, prevFallbackCol)
        if (nowStatus === 'added') sessionAdded++
        else if (nowStatus === 'dropped') sessionDropped++
        else sessionChanged++
    })

    const activeData = viewMode === 'baseline' ? baselineData : data
    const filtered = applyFilters(activeData, filters, recentlyTouched, baselineMap, prevCol, prevFallbackCol, exercise?.TEAM)

    // Stable sort: when no column override, keep the initial load order so row positions
    // don't jump after the user makes a change mid-session.
    const stableOrderMap = useMemo(
        () => new Map(stableOrder.map((id, i) => [id, i])),
        [stableOrder]
    )
    const sorted = sortOverride.key
        ? sortRows(filtered, sortOverride, prevCol, prevFallbackCol)
        : [...filtered].sort((a, b) => {
            const ia = stableOrderMap.get(a.PROF_ID) ?? Infinity
            const ib = stableOrderMap.get(b.PROF_ID) ?? Infinity
            return ia - ib
        })

    // Compute upgrade/downgrade counts from live row data for StatusBar guardrails
    // (used instead of a backend API call since v2 has all rows in memory)
    // DIR rows (Deceased/Inactive/Retired) are excluded — they don't count against limits
    //
    // excludeNTAddsFromCap: when true (TM21), NT→A/B/C upgrades don't count toward the
    // territory reclassification cap. Other teams (TM13, TM15 etc.) keep NT adds in the count.
    const _ptlConfig = exercise?.PER_TARGET_LIMITS ? JSON.parse(exercise.PER_TARGET_LIMITS) : null
    const excludeNTAddsFromCap = !!(_ptlConfig?.excludeNTAddsFromCap)
    const bypassLimits = !!(_ptlConfig?.noLimitsForSubstitute) && !!exercise?.vacantTerrID
    const visibleTypes = _ptlConfig
        ? Object.entries(_ptlConfig).filter(([k, v]) => v?.isVisible && k !== 'NT').map(([k]) => k)
        : ['A', 'B', 'C']
    const TYPE_RANK = Object.fromEntries(TYPE_ORDER.map((t, i) => [t, i]))
    const upgradesBySourceType = {}
    const downgradesBySourceType = {}
    let totalAllChanges = 0
    let totalNonNTChanges = 0
    data.forEach(row => {
        // Skip rows with no explicit change — avoids counting pre-exercise baseline
        // diffs (CURRENT_TARGET_VALUE ≠ PREV_TARGET_TYPE) as rep-made changes on first load
        if (!row.TARGET_TYPE && !row.CHANGED_TARGET_VALUE) return
        if (isDIR(row)) return  // DIR doesn't count against change or downgrade limits
        if (isMOT(row)) return  // MOT doesn't count against change or downgrade limits
        // Use CURRENT_TARGET_VALUE (this exercise's Q1) for guardrail counting.
        // PREV_TARGET_TYPE is from the prior exercise cycle and has no bearing on this cycle's limits.
        const q1 = row.CURRENT_TARGET_VALUE || 'NT'
        const q2 = row.TARGET_TYPE || row.CHANGED_TARGET_VALUE  // explicit rep change only, no Q1 fallback
        if (!q2 || q2 === q1) return  // reverted to original or no net change
        const isAdd  = q1 === 'NT' && q2 !== 'NT'   // NT → real type (upgrade from NT)
        const isDrop = q2 === 'NT'                   // real type → NT (drop)
        totalAllChanges++
        if (!isAdd) totalNonNTChanges++
        const sourceType = q1
        const isUpgrade  = isAdd || (!isDrop && TYPE_RANK[q2] < TYPE_RANK[q1])
        if (isUpgrade) {
            upgradesBySourceType[sourceType] = (upgradesBySourceType[sourceType] || 0) + 1
        } else {
            downgradesBySourceType[sourceType] = (downgradesBySourceType[sourceType] || 0) + 1
        }
    })
    const totalUpgradesInTerritory = Object.values(upgradesBySourceType).reduce((a, b) => a + b, 0)
    const totalDowngradesInTerritory = Object.values(downgradesBySourceType).reduce((a, b) => a + b, 0)

    // Baseline view is always read-only, regardless of the status-driven lock
    const effectiveLock = lock || viewMode === 'baseline'

    // DIR feature: enabled when the exercise config opts in via allowDIR in PER_TARGET_LIMITS
    const perTargetLimitsConfig = exercise?.PER_TARGET_LIMITS ? JSON.parse(exercise.PER_TARGET_LIMITS) : null
    const allowDIR = !!(perTargetLimitsConfig?.allowDIR)
    const allowMOT = !!(perTargetLimitsConfig?.allowMOT)

    if (!loaded && !status) return <Spinner />

    const terrLabel    = terrID || 'Territory'
    const exerciseName = exercise?.EXERCISE_NAME || exercise?.NAME || ''
    const rawDate      = exercise?.END_DATE || ''
    const windowClose  = rawDate
        ? new Date(rawDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : ''

    // Resolve column titles from config (mirrors v1 ResultTitles behaviour)
    const configCols = config ? Object.values(config) : []
    const configTitleFor = (col, fallback) =>
        configCols.find(c => c.col === col)?.title || fallback

    return (
        <div className={styles.page} style={{'--thead-top': `${controlsHeight}px`}}>
            {/* Skipped for approved territories/exercises; waits for status so it never flashes on an approved one */}
            <ExerciseGuidelinesModal
                open={showGuidelines && !!status && status.STATUS_ID !== 6 && !approved}
                onAcknowledge={() => setShowGuidelines(false)}
            />
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <span className={styles.terrLabel}>{terrLabel}</span>
                    {exerciseName && <><span className={styles.dot}> · </span><span className={styles.exerciseName}>{exerciseName}</span></>}
                    {windowClose  && <><span className={styles.dot}> · </span><span className={styles.windowClose}>Field window closes {windowClose}</span></>}
                </div>
                <div className={styles.headerRight}>
                    {config && (
                        <button
                            className={styles.downloadBtn}
                            onClick={handleExport}
                            title="Export to CSV"
                        >
                            <span aria-hidden="true">⬇</span>
                        </button>
                    )}
                    <div className={styles.toggle}>
                        <button
                            className={[styles.toggleBtn, viewMode === 'baseline' ? styles.activeBaseline : ''].join(' ')}
                            onClick={() => handleSetViewMode('baseline')}
                        >Baseline</button>
                        <button
                            className={[styles.toggleBtn, viewMode === 'live' ? styles.active : ''].join(' ')}
                            onClick={() => handleSetViewMode('live')}
                        >Live</button>
                    </div>
                </div>
            </div>

            <div ref={stickyControlsRef} className={styles.stickyControls}>
            <button
                className={[
                    styles.controlsCollapseToggle,
                    viewMode === 'baseline' ? styles.controlsCollapseToggleBaseline : ''
                ].filter(Boolean).join(' ')}
                onClick={() => setControlsCollapsed(c => !c)}
                title={controlsCollapsed ? 'Expand controls' : 'Collapse controls'}
            >
                <span>{controlsCollapsed ? '▼' : '▲'}</span>
                <span className={styles.collapseHint}>
                    {controlsCollapsed
                        ? `${sorted.length} rows · ${status ? Object.values(status.typeCounts || {}).reduce((s, v) => s + (v?.final || 0), 0) + ' targeted' : ''}`
                        : 'Collapse controls'
                    }
                </span>
                <span className={[styles.collapseViewBadge, viewMode === 'baseline' ? styles.collapseViewBadgeBaseline : styles.collapseViewBadgeLive].join(' ')}>
                    {viewMode === 'baseline' ? 'Baseline' : 'Live'}
                </span>
                {controlsCollapsed && (sessionAdded + sessionDropped + sessionChanged) > 0 && (
                    <span className={styles.collapseChangesBadge}>
                        {sessionAdded + sessionDropped + sessionChanged} change{(sessionAdded + sessionDropped + sessionChanged) !== 1 ? 's' : ''} this session
                    </span>
                )}
            </button>
            {!controlsCollapsed && (<>
            <KpiBar status={status} rows={activeData} allowDIR={allowDIR} prevCol={prevCol} prevFallbackCol={prevFallbackCol} visibleTypes={visibleTypes} />
            {viewMode === 'baseline' && (
                <div className={styles.baselineBanner}>
                    <span className={styles.baselineBannerText}>
                        📋 Viewing baseline — edits are not saved in this view
                    </span>
                    <button className={styles.baselineSwitchBtn} onClick={() => handleSetViewMode('live')}>
                        Switch to Live
                    </button>
                </div>
            )}

            <SearchBarV2
                filters={filters}
                onChange={setFilters}
                team={exercise?.TEAM}
                columnDefs={columnDefs}
                prevCol={prevCol}
                resultCount={sorted.length}
                totalCount={activeData.length}
                allowDIR={allowDIR}
                allowMOT={allowMOT}
                onResetColumns={isColumnCustomized ? handleResetColumns : null}
                visibleTypes={visibleTypes}
                columnPicker={
                    <ColumnPicker
                        columnDefs={columnDefs}
                        hiddenCols={hiddenCols}
                        onToggle={handleToggleCol}
                        onReset={handleResetColumns}
                        isCustomized={isColumnCustomized}
                    />
                }
            />

            <EditFootprintBanner
                added={sessionAdded}
                dropped={sessionDropped}
                typeChanges={sessionChanged}
                onResetToBaseline={handleResetToBaseline}
                lock={effectiveLock}
            />

            <TargetTypeLegend team={exercise?.TEAM} />

            <StatusBar
                status={status}
                perTargetTotals={perTargetTotals}
                id={exercise?.TARGETING_EXERCISE_ID}
                team={exercise?.TEAM}
                terr={terrID}
                getTotals={getTotals}
                user={user}
                exercise={exercise}
                approved={approved}
                lock={effectiveLock}
                upgradeCount={totalUpgradesInTerritory}
                downgradeCount={totalDowngradesInTerritory}
                upgradesBySourceType={upgradesBySourceType}
                downgradesBySourceType={downgradesBySourceType}
                changesExcludingNTAdds={excludeNTAddsFromCap ? totalNonNTChanges : totalAllChanges}
                bypassLimits={bypassLimits}
            />
            </>)}
            </div>

            {!loaded
                ? <Spinner />
                : (
                    <div className={[styles.tableWrap, viewMode === 'baseline' ? styles.tableWrapBaseline : ''].join(' ')}>
                        <table className={styles.table}>
                            <colgroup>
                                {orderedColDefs.map(def => (
                                    <col key={def.id} style={def.width ? { width: def.width } : undefined} />
                                ))}
                            </colgroup>
                            <thead>
                                <tr className={styles.thead}>
                                    {orderedColDefs.map(def => {
                                        const alignClass = def.align === 'center' ? styles.thCenter : def.align === 'right' ? styles.thRight : styles.thLeft
                                        const thClass = [
                                            alignClass,
                                            def.sortKey ? styles.sortable : '',
                                            styles.draggable,
                                            dragOverCol === def.id ? styles.dragOver : '',
                                        ].filter(Boolean).join(' ')
                                        return (
                                            <th
                                                key={def.id}
                                                className={thClass}
                                                title={def.title}
                                                style={def.width ? { width: def.width } : undefined}
                                                draggable
                                                onDragStart={e => handleDragStart(e, def.id)}
                                                onDragOver={e => handleDragOver(e, def.id)}
                                                onDrop={e => handleDrop(e, def.id)}
                                                onDragEnd={handleDragEnd}
                                                onClick={def.sortKey ? () => handleHeaderClick(def.sortKey) : undefined}
                                            >
                                                {def.title}{def.sortKey ? sortIndicator(def.sortKey) : null}
                                            </th>
                                        )
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {sorted.map((row, i) => (
                                    <HcpRow
                                        key={row.PROF_ID || i}
                                        row={row}
                                        baselineRow={baselineMap.get(row.PROF_ID)}
                                        exercise={exercise}
                                        user={user}
                                        terr={terrID}
                                        lock={effectiveLock}
                                        options={options}
                                        getTotals={getTotals}
                                        onRowUpdate={handleRowUpdate}
                                        onColumnReset={handleColumnReset}
                                        orderedColDefs={orderedColDefs}
                                        recentlyTouched={recentlyTouched}
                                        exerciseStatus={status}
                                        allowDIR={allowDIR}
                                        allowMOT={allowMOT}
                                        prevCol={prevCol}
                                        prevFallbackCol={prevFallbackCol}
                                    />
                                ))}
                            </tbody>
                        </table>
                        {sorted.length === 0 && <div className={styles.empty}>No results for this territory.</div>}
                    </div>
                )
            }

            {isDev && (
                <pre className={styles.devPanel}>
                    [DEV] Your role: {user?.role} · Your team: {user?.team}{'\n'}
                    [DEV] Exercise ID: {exercise?.ID} · Terr: {terrID} · View: {viewMode} · Rows: {sorted.length}
                </pre>
            )}
        </div>
    )
}

export default withAITracking(reactPlugin, TargetingV2)
